<template>
  <div class="ps-detail">
    <!-- Hero -->
    <div class="ps-hero">
      <v-container class="py-8">
        <router-link to="/mis-productos" class="ps-hero__back">
          <v-icon dark small>mdi-chevron-left</v-icon>
          <span>Volver al listado</span>
        </router-link>

        <div class="d-flex flex-wrap align-end mt-6" style="gap: 16px;">
          <div class="flex-grow-1">
            <v-chip
              v-if="product"
              :color="statusColor(product.status)"
              text-color="white"
              small
              class="font-weight-bold mb-2"
            >
              {{ statusLabel(product.status) }}
            </v-chip>
            <h1 class="ps-hero__title">{{ product ? product.name : "Cargando..." }}</h1>
            <p class="ps-hero__subtitle mb-0" v-if="product && product.shortDescription">
              {{ product.shortDescription }}
            </p>
          </div>

          <div class="d-flex flex-wrap" style="gap: 8px;" v-if="product">
            <v-btn
              v-if="product.status === 'DRAFT' || product.status === 'PAUSED'"
              color="secondary"
              depressed
              class="rounded-lg font-weight-bold ps-cta"
              :style="{ color: '#1b273e' }"
              :loading="actionLoading"
              @click="onPublish"
            >
              <v-icon left>mdi-rocket-launch</v-icon> Publicar
            </v-btn>
            <v-btn
              v-if="product.status === 'PUBLISHED'"
              outlined
              dark
              class="rounded-lg font-weight-bold"
              :loading="actionLoading"
              @click="onPause"
            >
              <v-icon left>mdi-pause</v-icon> Pausar
            </v-btn>
            <v-btn
              v-if="product.status !== 'ARCHIVED'"
              text
              dark
              class="rounded-lg font-weight-bold"
              :loading="actionLoading"
              @click="onArchive"
            >
              <v-icon left>mdi-archive-outline</v-icon> Archivar
            </v-btn>
            <v-btn
              v-if="product.status === 'PUBLISHED'"
              outlined
              dark
              class="rounded-lg font-weight-bold"
              @click="copyPublicLink"
            >
              <v-icon left>mdi-share-variant</v-icon> Compartir
            </v-btn>
          </div>
        </div>
      </v-container>
    </div>

    <!-- Tabs -->
    <v-container>
      <v-tabs
        v-model="tab"
        background-color="transparent"
        color="primaryVariant"
        slider-color="secondary"
        slider-size="4"
        class="ps-tabs"
      >
        <v-tab class="font-weight-bold text-none">Micrositio</v-tab>
        <v-tab class="font-weight-bold text-none">Ventas</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" class="transparent mt-4">
        <v-tab-item>
          <ProductServiceMicrositeTab
            v-if="product"
            :product="product"
            @updated="loadProduct"
          />
        </v-tab-item>
        <v-tab-item>
          <ProductServiceSalesTab v-if="product" :product-id="product._id" />
        </v-tab-item>
      </v-tabs-items>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";
import ProductServiceMicrositeTab from "@/components/myProductsServices/ProductServiceMicrositeTab.vue";
import ProductServiceSalesTab from "@/components/myProductsServices/ProductServiceSalesTab.vue";

export default {
  name: "ProductServiceDetail",
  components: { ProductServiceMicrositeTab, ProductServiceSalesTab },
  data() {
    return {
      tab: 0,
      actionLoading: false,
      placeholderBanner:
        "https://wechamber-public.s3.amazonaws.com/placeholders/product-service-placeholder.png",
    };
  },
  computed: {
    ...mapState("productServices", ["selectedProductService"]),
    product() {
      return this.selectedProductService;
    },
    bannerUrl() {
      return (this.product && this.product.bannerImage) || this.placeholderBanner;
    },
  },
  mounted() {
    this.loadProduct();
  },
  watch: {
    "$route.params.productServiceId"() {
      this.loadProduct();
    },
  },
  methods: {
    ...mapActions("productServices", [
      "fetchProductServiceById",
      "publishProductService",
      "pauseProductService",
      "archiveProductService",
    ]),
    ...mapMutations("notification", ["show"]),
    async loadProduct() {
      await this.fetchProductServiceById(this.$route.params.productServiceId);
    },
    statusLabel(status) {
      return ({
        DRAFT: "Borrador",
        PUBLISHED: "Publicado",
        PAUSED: "Pausado",
        ARCHIVED: "Archivado",
      })[status] || status;
    },
    statusColor(status) {
      return ({
        DRAFT: "grey",
        PUBLISHED: "success",
        PAUSED: "warning",
        ARCHIVED: "blue-grey",
      })[status] || "grey";
    },
    async onPublish() {
      this.actionLoading = true;
      const res = await this.publishProductService(this.product._id);
      this.actionLoading = false;
      if (res.status) this.show({ type: "success", text: "Producto publicado." });
      else this.show({ type: "error", text: res.message || "No se pudo publicar. Verifica audiencia y campos requeridos." });
    },
    async onPause() {
      this.actionLoading = true;
      const res = await this.pauseProductService(this.product._id);
      this.actionLoading = false;
      if (res.status) this.show({ type: "success", text: "Producto pausado." });
      else this.show({ type: "error", text: res.message || "No se pudo pausar el producto." });
    },
    async onArchive() {
      if (!confirm("¿Archivar este producto? No será visible para los miembros.")) return;
      this.actionLoading = true;
      const res = await this.archiveProductService(this.product._id);
      this.actionLoading = false;
      if (res.status) this.show({ type: "success", text: "Producto archivado." });
      else this.show({ type: "error", text: res.message || "No se pudo archivar el producto." });
    },
    copyPublicLink() {
      if (!this.product || !this.product.slug) return;
      const url = `${window.location.origin}/p/${this.product.slug}`;
      navigator.clipboard.writeText(url);
      this.show({ type: "success", text: "Enlace copiado al portapapeles." });
    },
  },
};
</script>

<style scoped>
.ps-detail {
  min-height: 100vh;
  background: #f5f6f8;
}
.ps-hero {
  background-color: #1b273e;
  background-size: cover;
  background-position: center;
  color: #ffffff;
  min-height: 240px;
}
.ps-hero__back {
  color: #ffffff;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  opacity: 0.9;
}
.ps-hero__title {
  color: #ffffff;
  font-weight: 700;
  font-size: 32px;
  letter-spacing: -0.5px;
  line-height: 1.1;
}
.ps-hero__subtitle {
  color: #e8eaef;
  font-size: 15px;
  max-width: 640px;
}
.ps-cta {
  text-transform: none;
  letter-spacing: 0;
}
.ps-tabs {
  border-bottom: 1px solid #e8eaef;
}
</style>
